import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { OBJECTIVES } from "@/lib/campaignMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Eye, Trash2, FolderKanban, Calendar, Sparkles, DollarSign, MapPin, Megaphone, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Math.round(n).toLocaleString()}`;

// Derive the compact card facts from a saved campaign. Pure read of persisted fields;
// never substitutes nationwide targeting or a fabricated center when markets are missing.
function cardMeta(c) {
  const snap = c.strategy_snapshot || null;
  const strat = snap?.organic_paid_strategy || null;
  const budget = typeof c.budget === "number" ? c.budget : null;
  const hasBudget = budget != null && budget > 0;
  const isOrganicOnly = strat === "organic";
  const isPaid = !isOrganicOnly && (strat === "organic_and_paid" || strat === "paid" || hasBudget);

  const t = c.targeting || {};
  const markets = Array.isArray(t.markets) ? t.markets.filter(Boolean) : [];
  const radius = t.radius_miles != null ? t.radius_miles : null;
  const center = c.territory || markets[0] || null;

  let geoText, geoWarn = false;
  if (markets.length) {
    geoText = markets.join(", ") + (radius ? ` \u00b7 ${radius} mi radius` : "");
  } else if (radius && center) {
    geoText = `${center} \u00b7 ${radius} mi radius`;
  } else if (radius && !center) {
    geoText = `${radius} mi radius, center not set`;
    geoWarn = true;
  } else {
    geoText = "No markets set";
    geoWarn = isPaid;
  }

  const missing = [];
  if (isPaid) {
    if (!hasBudget) missing.push("budget");
    if (!(markets.length || (radius && center))) missing.push("geographic markets");
  }

  let status, statusTone;
  if (isOrganicOnly) { status = "Ready for Review"; statusTone = "ready"; }
  else if (isPaid) {
    if (missing.length) { status = "Needs Configuration"; statusTone = "warn"; }
    else { status = "Ready for Review"; statusTone = "ready"; }
  } else { status = "Draft"; statusTone = "draft"; }

  return {
    typeLabel: isOrganicOnly ? "Organic" : (isPaid ? "Paid" : null),
    isPaid, isOrganicOnly,
    budgetText: isPaid ? (hasBudget ? money(budget) : null) : null,
    budgetWarn: isPaid && !hasBudget,
    geoText, geoWarn, status, statusTone, missing,
  };
}

export default function CampaignLibrary() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [obj, setObj] = useState("all");
  const [sort, setSort] = useState("newest");
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    let list = campaigns.filter((c) => {
      const text = `${c.title} ${c.boat_snapshot?.manufacturer} ${c.boat_snapshot?.model}`.toLowerCase();
      if (q && !text.includes(q.toLowerCase())) return false;
      if (obj !== "all" && c.objective !== obj) return false;
      return true;
    });
    list = [...list].sort((a, b) => sort === "newest"
      ? new Date(b.created_at) - new Date(a.created_at)
      : new Date(a.created_at) - new Date(b.created_at));
    return list;
  }, [campaigns, q, obj, sort]);

  const view = (c) => {
    const snap = c.strategy_snapshot || null;
    const recjet = snap ? {
      category: snap.category,
      recommended_budget: snap.organic_paid_strategy === "organic" ? null : snap.dealer_spend,
      budget_context: snap.budget_context || null,
      organic_paid_strategy: snap.organic_paid_strategy,
      spend_explanation: snap.spend_explanation,
      geo_targeting: snap.geo_targeting || null,
      best_fit_buyer: snap.best_fit_buyer,
    } : null;
    navigate("/campaign-results", {
      state: {
        results: c.results,
        boat: { ...c.boat_snapshot, id: c.boat_id },
        config: { boat_id: c.boat_id, objective: c.objective, tone: c.tone, selected_photos: c.selected_photos,
                  budget: c.budget, targeting: c.targeting, territory: c.territory,
                  strategy_source: c.strategy_source, strategy_snapshot: c.strategy_snapshot, recjet },
        recjet,
      },
    });
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/campaigns/${deleteId}`);
      toast.success("Campaign deleted");
      setDeleteId(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="campaign-library-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-navy">Campaign Library</h1>
          <p className="text-muted-foreground mt-1">{campaigns.length} saved campaigns</p>
        </div>
        <Button onClick={() => navigate("/create-campaign")} className="gold-gradient text-navy font-bold h-11" data-testid="library-create-btn">
          <Sparkles className="w-4 h-4 mr-2" /> Create Campaign
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by boat…" className="pl-9 h-11 bg-white" data-testid="library-search" />
        </div>
        <Select value={obj} onValueChange={setObj}>
          <SelectTrigger className="w-full md:w-52 h-11 bg-white" data-testid="library-objective-filter"><SelectValue placeholder="Objective" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Objectives</SelectItem>
            {OBJECTIVES.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-full md:w-40 h-11 bg-white" data-testid="library-sort"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-20 text-muted-foreground">Loading campaigns…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground bg-white rounded-2xl border border-border">
          <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="font-semibold text-navy">No campaigns yet</p>
          <p className="text-sm">Generate and save a campaign to see it here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c) => {
            const m = cardMeta(c);
            const statusCls = m.statusTone === "ready" ? "bg-green-100 text-green-700"
              : m.statusTone === "warn" ? "bg-amber-100 text-amber-700" : "bg-secondary text-muted-foreground";
            return (
            <div key={c.id} className="bg-white rounded-2xl border border-border overflow-hidden card-hover flex flex-col" data-testid={`campaign-card-${c.id}`}>
              <div className="relative h-40 bg-secondary">
                {(c.selected_photos?.[0] || c.boat_snapshot?.photos?.[0]) ? (
                  <img src={resolveImg(c.selected_photos?.[0] || c.boat_snapshot?.photos?.[0])} alt="" className="w-full h-full object-cover" />
                ) : <div className="w-full h-full flex items-center justify-center"><FolderKanban className="w-8 h-8 opacity-30" /></div>}
                <span className="absolute top-3 left-3 text-xs font-semibold uppercase tracking-wider text-navy bg-gold px-3 py-1 rounded-full">{c.objective_label}</span>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-bold text-navy leading-tight" data-testid={`campaign-boat-${c.id}`}>{c.boat_snapshot?.year} {c.boat_snapshot?.manufacturer} {c.boat_snapshot?.model}</h3>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                  <Calendar className="w-3.5 h-3.5" /> {new Date(c.created_at).toLocaleDateString()} · {c.tone}
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {m.typeLabel && (
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${m.typeLabel === "Paid" ? "bg-navy/10 text-navy" : "bg-teal-100 text-teal-700"}`} data-testid={`campaign-type-${c.id}`}>
                      <Megaphone className="w-3 h-3" /> {m.typeLabel}
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusCls}`} data-testid={`campaign-status-${c.id}`}>
                    {m.statusTone === "ready" ? <CheckCircle2 className="w-3 h-3" /> : m.statusTone === "warn" ? <AlertCircle className="w-3 h-3" /> : null}
                    {m.status}
                  </span>
                </div>

                <div className="mt-3 space-y-1.5 text-sm">
                  {m.isPaid && (
                    <div className="flex items-center gap-2" data-testid={`campaign-budget-${c.id}`}>
                      <DollarSign className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      {m.budgetWarn
                        ? <span className="text-amber-600 font-medium">Budget not set</span>
                        : <span className="text-navy">Proposed budget: <span className="font-semibold">{m.budgetText}</span></span>}
                    </div>
                  )}
                  <div className="flex items-center gap-2" data-testid={`campaign-geo-${c.id}`}>
                    <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className={`truncate ${m.geoWarn ? "text-amber-600 font-medium" : "text-navy"}`}>{m.geoText}</span>
                  </div>
                </div>

                {m.status === "Needs Configuration" && m.missing.length > 0 && (
                  <p className="mt-2 text-[11px] text-amber-600" data-testid={`campaign-needs-${c.id}`}>Needs: {m.missing.join(" and ")}</p>
                )}

                <div className="flex gap-2 pt-3 mt-auto">
                  <Button onClick={() => view(c)} className="flex-1 bg-navy hover:bg-navy-light text-white" data-testid={`view-campaign-${c.id}`}>
                    <Eye className="w-4 h-4 mr-1.5" /> View
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => setDeleteId(c.id)} className="hover:text-red-600 hover:border-red-300" data-testid={`delete-campaign-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this campaign?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove the saved campaign. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-campaign-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-campaign-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
